import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import type { ChatTurn } from '../hooks/useChat';
import { IconStop, IconImage } from './Icon';
import { useElapsed } from '../lib/use-elapsed';
import { useLanguage, pick } from '../lib/language';

/**
 * Default message fired on first mount of the Studio when there is no
 * current image. Kept in one place so prompt-engineer-style tweaks stay
 * in sync with the backend's system prompt.
 */
export const DEFAULT_FIRST_MESSAGE = 'Generate a default eye reference';

/**
 * v6-polish — suggested chat prompts shown when the chat is empty so
 * mum can click a chip instead of staring at a blank textarea. Hidden
 * in technical mode (Ty prefers to write his own prompts). Plain
 * language, aphantasia-friendly.
 */
const SUGGESTED_PROMPTS = [
  'Try an angry expression with red light',
  'Make a soft, romantic anime eye',
  'Try a realistic human portrait eye',
  'Black-and-white inked comic eye',
];

export interface ChatPanelProps {
  history: ChatTurn[];
  /** True while a chat request is in flight; disables the input. */
  sending: boolean;
  /** Optional error message to surface as a small banner above the input. */
  errorMessage?: string | null;
  /** Called when the user submits a new message. */
  onSend: (message: string) => void | Promise<void>;
  /**
   * v5 — fired when the user clicks the Stop button while a request is
   * in flight. Studio.tsx wires this to `useChat.cancel()`. Optional —
   * when omitted, the Stop button is hidden so non-Studios (e.g. the
   * heritage testing page) keep the simple Send-only surface.
   */
  onCancel?: () => void;
  /** Visible label for the input (e.g. "Ask for a change"). */
  promptLabel?: string;
  /** Placeholder text inside the textarea. */
  placeholder?: string;
  /**
   * Optional trailing slot in the header — used by Studio.tsx to surface a
   * "Fine-tune by hand" disclosure so the chat-first user can opt into
   * manual controls without leaving the chat surface.
   */
  headerTrailing?: ReactNode;
  /**
   * v7 — called when the user picks a reference image via the file input
   * shown above the textarea. The Studio is responsible for uploading the
   * file via api.uploadReference() and storing the returned URL in its own
   * state so it can be passed on the next chat-with-reference call.
   */
  onUploadReference?: (file: File) => void | Promise<void>;
  /**
   * v7 — when a reference has been uploaded, show its thumbnail above the
   * textarea. Cleared by the parent once the next chat-with-reference call
   * has been issued.
   */
  referencePreviewUrl?: string | null;
  /** v7 — clear the reference thumbnail (the user can re-upload or drop it). */
  onClearReference?: () => void;
}

/**
 * Chat-first control surface. A scrollable history of `{role, content}`
 * turns with a sticky input row at the bottom. Submits on Enter
 * (Shift+Enter inserts a newline). Auto-scrolls to the latest turn.
 *
 * v5 a11y:
 *  - `role="log"` is intentionally NOT applied to the whole history. That
 *    would make screen readers announce every keystroke / scroll-into-view
 *    as messages arrive. Instead only the latest assistant message lives
 *    inside an `aria-live="polite"` region so the AT announces the new
 *    AI response without spamming every intermediate state.
 *  - Focus-on-send only snaps back to the input if focus was already
 *    inside the chat panel (the user wasn't browsing the page). Otherwise
 *    a click on, e.g., the eye preview would be silently hijacked.
 *  - The textarea is `aria-required="true"` with a visible asterisk.
 *
 * v6-polish:
 *  - Suggested-prompt chips above the input, plain mode only.
 *  - aria-describedby on the textarea so the keyboard-shortcut hint is
 *    programmatically associated (screen readers read it on focus).
 *  - Loading-state progress bar with elapsed time, plain mode only.
 *    Technical mode keeps the terse "Generating the next reference…".
 */
export function ChatPanel(props: ChatPanelProps) {
  const {
    history,
    sending,
    errorMessage,
    onSend,
    onCancel,
    promptLabel = 'Ask for a change',
    placeholder = 'e.g. make the iris more golden, add a fire reflection',
    headerTrailing,
    onUploadReference,
    referencePreviewUrl,
    onClearReference,
  } = props;

  const { lang } = useLanguage();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Stable id for the keyboard-shortcut hint, used by aria-describedby.
  const hintId = useId();

  const elapsed = useElapsed(sending);

  // Auto-scroll to the bottom whenever history length grows or sending state flips.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [history.length, sending]);

  // After a send completes, restore focus to the input ONLY if focus is
  // still inside the chat panel. If the user clicked elsewhere (eye
  // preview, header, etc.) we must not steal focus back.
  useEffect(() => {
    if (sending) return;
    const panel = panelRef.current;
    if (!panel) return;
    const active = document.activeElement;
    if (active && (active === inputRef.current || panel.contains(active))) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [sending]);

  const submit = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setDraft('');
    void onSend(trimmed);
  }, [draft, onSend]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        submit();
      }
    },
    [submit],
  );

  // v6-polish — clicking a suggested chip populates the input and
  // focuses it so the user can edit (or just hit Enter to send).
  const applyChip = useCallback(
    (text: string) => {
      setDraft(text);
      // requestAnimationFrame waits for React to flush the new value
      // before we move focus + selection. Without it, focus() lands
      // but the cursor is at position 0 instead of the end of the
      // inserted text.
      window.requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(text.length, text.length);
      });
    },
    [],
  );

  const disabled = sending || draft.trim().length === 0;

  // The index of the last assistant message — only that <li> gets an
  // aria-live wrapper. Older messages are silent.
  const lastAssistantIdx = (() => {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'assistant') return i;
    }
    return -1;
  })();

  const isPlain = lang === 'plain';
  const showSuggestedPrompts = isPlain && history.length === 0;

  // Loading-state copy. Plain mode gets the friendly progress language;
  // technical mode keeps the terse single-line status.
  const generatingStatus = pick(lang, {
    plain: `Generating… usually takes 8–15 seconds`,
    technical: 'Generating the next reference…',
  });
  const elapsedHint = pick(lang, {
    plain: ` ${elapsed}s elapsed`,
    technical: null as string | null,
  });
  const showProgressBar = isPlain && sending;

  return (
    <section
      ref={panelRef}
      aria-label="Chat with the AI"
      className="flex h-full min-h-[260px] flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900 lg:max-h-[640px] lg:overflow-hidden"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          Chat with the AI
        </h2>
        <div className="flex items-center gap-3">
          {headerTrailing}
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {sending
              ? 'Thinking…'
              : history.length > 0
                ? `${history.length} turn${history.length === 1 ? '' : 's'}`
                : 'Empty'}
          </span>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="min-h-[120px] flex-1 overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-950"
      >
        {history.length === 0 ? (
          <p className="text-neutral-500 dark:text-neutral-400">
            No messages yet. Type what you'd like to change — the AI will
            translate it into reflection settings and update the preview.
          </p>
        ) : (
          <ol className="flex flex-col gap-3">
            {history.map((turn, idx) => {
              const isLatestAssistant = idx === lastAssistantIdx;
              return (
                <li
                  key={`${turn.role}-${idx}-${turn.content.slice(0, 8)}`}
                  data-role={turn.role}
                  {...(isLatestAssistant
                    ? { 'aria-live': 'polite' as const, 'aria-atomic': 'true' as const }
                    : {})}
                  className={[
                    'flex flex-col gap-1 rounded-lg px-3 py-2',
                    turn.role === 'user'
                      ? 'self-end bg-accent/15 text-neutral-900 dark:text-neutral-50'
                      : 'self-start bg-neutral-100 text-neutral-800 italic dark:bg-neutral-800 dark:text-neutral-100',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'text-[10px] font-semibold uppercase tracking-wide',
                      turn.role === 'user'
                        ? 'text-accent-light dark:text-[#a8c0ff]'
                        : 'text-neutral-500 dark:text-neutral-400',
                    ].join(' ')}
                  >
                    {turn.role === 'user' ? 'You' : 'AI'}
                  </span>
                  <span className="whitespace-pre-wrap text-sm leading-snug">
                    {turn.content}
                  </span>
                  {/* v7 — plain-language description; plain mode only, shown as a
                      small subtitle below the AI's reply. Used by aphantasia
                      browsers who can't picture the image from the diff alone. */}
                  {turn.role === 'assistant' && turn.description && isPlain ? (
                    <span
                      data-testid="chat-turn-description"
                      className="mt-1 not-italic text-[12px] leading-snug text-neutral-600 dark:text-neutral-300"
                    >
                      {turn.description}
                    </span>
                  ) : null}
                  {/* v7 — for variation-pick turns, render a compact list of
                      all 4 candidates with the chosen one highlighted. */}
                  {turn.role === 'assistant' && turn.variation ? (
                    <ul
                      className="mt-1 flex flex-wrap gap-1 not-italic"
                      aria-label="Variations in this turn"
                    >
                      {turn.variation.items.map((v, vidx) => {
                        const chosen = v.id === turn.variation!.selected_id;
                        return (
                          <li
                            key={v.id}
                            className={[
                              'rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
                              chosen
                                ? 'border-accent bg-accent/15 font-semibold text-accent'
                                : 'border-neutral-300 text-neutral-500 dark:border-neutral-700 dark:text-neutral-400',
                            ].join(' ')}
                          >
                            Var {vidx + 1}{chosen ? ' • chosen' : ''}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
            {sending ? (
              <li
                aria-hidden="true"
                className="flex flex-col gap-2 self-start rounded-lg bg-neutral-100 px-3 py-2 text-xs text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="h-2 w-2 motion-safe:animate-pulse rounded-full bg-accent" />
                  <span>{generatingStatus}{elapsedHint}</span>
                </span>
                {showProgressBar ? (
                  // v6-polish — indeterminate progress bar. CSS animation
                  // (translate-x: -50% → 100%) makes the bar glide across
                  // the track. No JS time math needed.
                  <div
                    role="progressbar"
                    aria-label="Generation progress"
                    aria-valuetext={`${elapsed} seconds elapsed`}
                    className="relative h-1 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-accent motion-safe:animate-[progress-glide_1.4s_ease-in-out_infinite]"
                    />
                  </div>
                ) : null}
              </li>
            ) : null}
          </ol>
        )}
      </div>

      {/* v6-polish — suggested chat prompts. Plain mode only, hidden once
          the chat has turns so the user isn't re-shown suggestions after
          they've started a conversation. */}
      {showSuggestedPrompts ? (
        <div
          aria-label="Suggested chat prompts"
          className="flex flex-col gap-2 rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-950"
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
            Try one of these to start
          </span>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => applyChip(prompt)}
                className="inline-flex h-9 max-w-full cursor-pointer items-center rounded-full border border-neutral-300 bg-white px-3 text-left text-xs text-neutral-800 motion-safe:transition-colors hover:border-accent hover:bg-accent/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-accent dark:hover:bg-accent/15 dark:focus-visible:ring-offset-neutral-950"
              >
                <span className="line-clamp-1">{prompt}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {errorMessage ? (
        <p
          role="alert"
          className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-700 dark:bg-rose-900/30 dark:text-rose-100"
        >
          {errorMessage}
        </p>
      ) : null}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-2"
        aria-label="Send a new chat message"
      >
        {/* v7 — reference image upload affordance. Shows the uploaded
            thumbnail (if any) above the textarea + an "Attach reference"
            button. Plain mode hides the "Reference image" technical copy. */}
        {onUploadReference ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
                {lang === 'plain' ? 'Reference image (optional)' : 'Reference (optional)'}
              </span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                data-testid="chat-panel-attach-reference"
                className="inline-flex h-9 cursor-pointer items-center gap-1 rounded-md border border-neutral-300 bg-white px-2 text-xs font-medium text-neutral-800 hover:border-accent hover:bg-accent/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
              >
                <IconImage size={14} aria-hidden />
                <span>{referencePreviewUrl
                  ? lang === 'plain' ? 'Replace' : 'Replace reference'
                  : lang === 'plain' ? 'Upload reference image' : 'Attach reference'}</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) await onUploadReference(file);
                  e.target.value = '';
                }}
              />
            </div>
            {referencePreviewUrl ? (
              <div
                data-testid="chat-panel-reference-thumbnail"
                className="flex items-center gap-2 rounded-md border border-accent/40 bg-accent/10 p-2"
              >
                <img src={referencePreviewUrl} alt="Reference image" className="h-12 w-12 rounded-md object-cover" />
                <div className="flex flex-1 flex-col text-[11px] text-neutral-700 dark:text-neutral-300">
                  <span className="font-medium">{lang === 'plain' ? 'Reference attached' : 'Reference image attached'}</span>
                  <span className="text-neutral-500 dark:text-neutral-400">{lang === 'plain' ? 'Sent with your next message' : 'Will be passed as input_urls to MiniMax'}</span>
                </div>
                {onClearReference ? (
                  <button
                    type="button"
                    onClick={onClearReference}
                    aria-label="Remove reference"
                    className="inline-flex h-9 cursor-pointer items-center rounded-md border border-neutral-300 bg-white px-2 text-xs text-neutral-700 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                  >
                    {lang === 'plain' ? 'Remove' : 'Clear'}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <label
          htmlFor="chat-input"
          className="flex items-center gap-1 text-xs font-medium text-neutral-700 dark:text-neutral-300"
        >
          <span>{promptLabel}</span>
          <span aria-hidden="true" className="text-rose-600">*</span>
          <span className="sr-only">required</span>
        </label>
        <textarea
          ref={inputRef}
          id="chat-input"
          name="chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          rows={2}
          placeholder={placeholder}
          disabled={sending}
          aria-busy={sending}
          aria-required="true"
          aria-label={promptLabel}
          // v6-polish — programmatic link from input → keyboard hint so
          // screen readers announce the Enter / Shift+Enter rules when
          // the user lands on the field.
          aria-describedby={hintId}
          className="min-h-[44px] resize-none rounded-md border border-neutral-300 bg-white p-3 text-sm text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
        />
        <div className="flex items-center justify-between gap-2">
          <span id={hintId} className="text-[11px] text-neutral-500 dark:text-neutral-400">
            Press <kbd className="rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">Enter</kbd> to send,
            <kbd className="ml-1 rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">Shift</kbd>+<kbd className="rounded border border-neutral-300 px-1 font-mono dark:border-neutral-600">Enter</kbd> for newline.
          </span>
          {sending && onCancel ? (
            // v5 — replace Send with Stop while a request is in flight so
            // mum can abort without waiting for the response. Only rendered
            // when an onCancel handler was supplied by the parent.
            <button
              type="button"
              onClick={onCancel}
              aria-label="Stop generating"
              className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-5 text-sm font-semibold text-rose-800 hover:bg-rose-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:border-rose-700 dark:bg-rose-900/40 dark:text-rose-100 dark:hover:bg-rose-900/60 dark:focus-visible:ring-offset-neutral-950"
            >
              <IconStop size={14} aria-hidden />
              Stop
            </button>
          ) : (
            <button
              type="submit"
              disabled={disabled}
              aria-busy={sending}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md bg-accent px-5 text-sm font-semibold text-neutral-950 motion-safe:transition-colors hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 dark:focus-visible:ring-offset-neutral-950"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}